import { FC, useCallback, useState } from 'react';
import { ChartDataset, ChartData as ChartDataType, Point } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { MultiChartType, ChartPoint, TypedChartData } from '../../types';
import { AxisTransformer, getDefaultChartOptions, getDefaultChartPlugins } from '../../utils';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { DEFAULT_COLORS } from '../../constants';
import { ChartError } from '../ChartError';

interface BarLineChartProps {
  data: TypedChartData[];
  chartId: string;
  xAxisLabel: string;
  yAxisLabel: string;
  yAxisTransformer?: AxisTransformer;
  xAxisTransformer?: AxisTransformer;
  y1AxisLabel?: string;
  y1AxisTransformer?: AxisTransformer;
  renderForExport?: boolean;
}

// TODO: implement common util function for converting all bar types and line charts
const transformDataForChart = (
  data: TypedChartData[],
  colors: string[] = DEFAULT_COLORS,
): ChartDataType<'line' | 'bar'> => {
  const lineCharts: ChartDataset<'line', (number | Point | [number, number] | null)[]>[] = [];
  const barCharts: ChartDataset<'bar', (number | Point | [number, number] | null)[]>[] = [];

  for (const [index, chart] of data.entries()) {
    if (chart.type === MultiChartType.LINE_CHART) {
      const chartData = {
        type: 'line' as const,
        data: chart.points.map((point: ChartPoint) => Number(point.y)),
        label: chart.name,
        borderColor: colors[index % colors.length],
        backgroundColor: colors[index % colors.length],
        pointRadius: 4,
        pointBackgroundColor: 'white',
        pointBorderWidth: 2,
        yAxisID: 'y1',
      };
      lineCharts.push(chartData);
    } else if (chart.type === MultiChartType.BAR_CHART) {
      const chartData = {
        type: 'bar' as const,
        data: chart.points.map((point: ChartPoint) => Number(point.y)),
        label: chart.name,
        backgroundColor: colors[index % colors.length],
        barThickness: 33,
      };
      barCharts.push(chartData);
    }
  }

  return {
    labels: data?.[0]?.points?.map((point: ChartPoint) => point.x),
    datasets: [...lineCharts, ...barCharts],
  };
};

const BarLineChart: FC<BarLineChartProps> = ({
  data,
  chartId,
  xAxisLabel,
  yAxisLabel,
  y1AxisLabel,
  y1AxisTransformer,
  yAxisTransformer,
  xAxisTransformer,
  renderForExport,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [chartNode, setChartNode] = useState<HTMLDivElement | null>(null);

  // Callback ref to assign the ref value dynamically
  const chartRef = useCallback((node: HTMLDivElement | null) => {
    setChartNode(node);
  }, []);

  // Use the intersection observer hook for performance
  const isVisible = useIntersectionObserver(chartNode);

  // Transform data
  const chartData = transformDataForChart(data, DEFAULT_COLORS);

  const xAxisTicksCallback = (value: any, index: number) => {
    return chartData.labels?.[index] || value;
  };

  // Merge default options with custom options
  const chartOptions = {
    ...getDefaultChartOptions({
      xAxisLabel,
      yAxisTransformationFuntion: yAxisTransformer,
      y1AxisTransformationFuntion: y1AxisTransformer,
      xAxisTransformationFuntion: xAxisTransformer,
      y1Axis: true,
      xAxisTicksCallback,
      tooltipConfigs: {
        bodyConfigs: [
          {
            type: 'indicator',
          },
          {
            type: 'label',
          },
          {
            type: 'value',
          },
        ],
      },
    }),
  };

  const chartPlugins = [
    ...getDefaultChartPlugins({
      xLabel: xAxisLabel,
      yLabel: yAxisLabel,
      y1Label: y1AxisLabel,
    }),
  ];

  const totalDataPoints = chartData.datasets.reduce((acc, dataset) => acc + dataset.data.length, 0);

  if (totalDataPoints === 0) {
    return <ChartError error="No data points found" />;
  }

  return (
    <>
      <Box
        id={`pvx-line-chart-container-${chartId}`}
        ref={chartRef}
        sx={{ height: isMobile ? '400px' : '620px', width: '100%' }}
      >
        {renderForExport ? (
          <Chart type="bar" data={chartData} options={chartOptions as any} plugins={chartPlugins} />
        ) : (
          isVisible && (
            <Chart
              type="bar"
              data={chartData}
              options={chartOptions as any}
              plugins={chartPlugins}
            />
          )
        )}
      </Box>
    </>
  );
};

export default BarLineChart;
